import type { ReactNode } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Tooltip from "@/v2-ui/tooltip"
import { formatInvoiceAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import type { RenewalStats } from "@/v2-backoffice-client/license/renewals/license.renewals.utils"

export type LicenseRenewalStatsProps = {
  stats: RenewalStats
}

// The headline numbers over the cashflow chart. Every card states its
// window and basis in the subtext and explains its exact definition on
// hover — a number an operator can repeat in a meeting without guessing
// what it covers.
function LicenseRenewalStats(props: LicenseRenewalStatsProps) {
  const { stats } = props

  const runRateDeltaPct =
    stats.runRateNow > 0
      ? Math.round(
          ((stats.runRateEnd - stats.runRateNow) / stats.runRateNow) * 100,
        )
      : null

  return (
    <div className="grid gap-4 sm:grid-cols-4">
      <StatCard
        label="Cash next 90 days"
        hint="Every invoice the committed book generates inside 90 days: renewals (including at-risk ones) and scheduled starts. Churn generates nothing and is excluded — as are trials and unsold deals, which have no billing behind them."
      >
        <p className="text-sm font-semibold tabular-nums">
          {formatInvoiceAmount(Math.round(stats.next90Amount), stats.currency)}
        </p>
        <p className="text-xs text-muted-foreground">
          {stats.next90Count} invoice{stats.next90Count === 1 ? "" : "s"}{" "}
          landing
        </p>
      </StatCard>

      <StatCard
        label="Starting"
        hint="Subscriptions already created in Fenerum with a start date ahead — committed new cashflow, not pipeline. The per-month figure is their combined run-rate once every one of them has started billing."
      >
        <p className="text-sm font-semibold tabular-nums">
          +{formatInvoiceAmount(Math.round(stats.startingMrr), stats.currency)}
          <span className="font-normal text-muted-foreground">/mo</span>
        </p>
        <p className="text-xs text-muted-foreground">
          {stats.startingCount} scheduled subscription
          {stats.startingCount === 1 ? "" : "s"}
          {stats.firstStartDate &&
            ` · first ${formatDateString(stats.firstStartDate.toISOString())}`}
        </p>
      </StatCard>

      <StatCard
        label="Churning"
        hint="Subscriptions set not to renew in Fenerum. Each keeps billing until its own final term ends — the red chart bars mark the month each departure lands, between the first and last date below. The per-month figure is the run-rate the book loses once ALL of them have lapsed; the Event column's context explains why each one is leaving."
      >
        <p
          className={cn(
            "text-sm font-semibold tabular-nums",
            stats.churningMrr > 0 && "text-destructive",
          )}
        >
          −{formatInvoiceAmount(Math.round(stats.churningMrr), stats.currency)}
          <span className="font-normal text-muted-foreground">/mo</span>
        </p>
        <p className="text-xs text-muted-foreground">
          {stats.churningCount} subscription
          {stats.churningCount === 1 ? "" : "s"} end at their final term
          {stats.firstChurnDate &&
            stats.lastChurnDate &&
            ` · ${formatDateString(stats.firstChurnDate.toISOString())} – ${formatDateString(stats.lastChurnDate.toISOString())}`}
        </p>
      </StatCard>

      <StatCard
        label={`Run-rate in ${stats.horizonMonths} months`}
        hint="The committed book's monthly billing at the end of the window: today's run-rate, plus scheduled subscriptions as they start, minus churned ones as their final terms lapse. New sales and trial conversions are deliberately NOT forecast — this is the floor, not the target."
      >
        <p className="text-sm font-semibold tabular-nums">
          {formatInvoiceAmount(Math.round(stats.runRateEnd), stats.currency)}
          <span className="font-normal text-muted-foreground">/mo</span>
        </p>
        <p
          className={cn(
            "text-xs",
            runRateDeltaPct != null && runRateDeltaPct < 0
              ? "text-destructive"
              : "text-muted-foreground",
          )}
        >
          {runRateDeltaPct != null
            ? `${runRateDeltaPct >= 0 ? "+" : ""}${runRateDeltaPct} % vs today · committed book only`
            : "committed book only"}
        </p>
      </StatCard>
    </div>
  )
}

// One stat card: label with an explain-on-hover info glyph, then the value
// and subtext the caller renders.
function StatCard(props: { label: string; hint: string; children: ReactNode }) {
  const { label, hint, children } = props
  return (
    <section className="flex flex-col gap-1 border-l-2 border-border pl-3">
      <h3 className="flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        <Tooltip>
          <Tooltip.Trigger asChild>
            <span className="cursor-help">
              <Icon src="info" size={12} />
            </span>
          </Tooltip.Trigger>
          <Tooltip.Content side="top" className="ui-tooltip-content--panel">
            <p className="max-w-xs p-3 text-xs text-muted-foreground">{hint}</p>
          </Tooltip.Content>
        </Tooltip>
      </h3>
      {children}
    </section>
  )
}

export default LicenseRenewalStats
